import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, LifeBuoy, MessageSquare, Pencil, Play, RotateCcw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import { formatDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useCommentOnItem,
  useMoveWorkItem,
  useSaveTimeEntry,
  useStartTimer,
  useWorkItem,
} from './api';
import { WorkItemDialog } from './work-item-dialog';
import {
  ACTIVITY_KIND_LABELS,
  WORK_ITEM_PRIORITY_LABELS,
  WORK_ITEM_PRIORITY_VARIANT,
  WORK_ITEM_STATUS_LABELS,
  WORK_ITEM_TYPE_COLOUR,
  WORK_ITEM_TYPE_LABELS,
  formatMinutes,
  parseHoursInput,
} from './labels';
import type { WorkItemStatus } from './types';

const STATUSES: WorkItemStatus[] = [
  'BACKLOG',
  'TODO',
  'IN_PROGRESS',
  'CODE_REVIEW',
  'QA',
  'UAT',
  'DONE',
  'DEPLOYED',
];

export function WorkItemSheet({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: item, isLoading } = useWorkItem(id);
  const move = useMoveWorkItem();
  const comment = useCommentOnItem(id);
  const logTime = useSaveTimeEntry();
  const startTimer = useStartTimer();

  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState('');
  const [hours, setHours] = useState('');
  const [logNote, setLogNote] = useState('');
  const [activityKind, setActivityKind] = useState('DEVELOPMENT');
  const [error, setError] = useState<string | null>(null);

  if (isLoading || !item) return null;

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              <span className={cn('text-xs font-bold uppercase', WORK_ITEM_TYPE_COLOUR[item.itemType])}>
                {WORK_ITEM_TYPE_LABELS[item.itemType]}
              </span>
              <span className="font-mono text-sm text-muted-foreground">{item.key}</span>
              <span>{item.title}</span>
            </DialogTitle>
            <DialogDescription>
              {item.project.name}
              {item.sprint && ` · ${item.sprint.name}`}
              {item.reopenCount > 0 && (
                <span className="ml-2 text-amber-600">
                  reopened {item.reopenCount} {item.reopenCount === 1 ? 'time' : 'times'}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
            <div className="flex flex-wrap items-center gap-2">
              <Can permission="project.task.update">
                <Select
                  className="h-8 w-auto text-xs"
                  value={item.status}
                  onChange={async (event) => {
                    setError(null);
                    try {
                      await move.mutateAsync({ id, status: event.target.value });
                    } catch (caught) {
                      const failure = caught as { message?: string; details?: { message: string }[] };
                      setError(
                        failure.details?.[0]?.message ?? failure.message ?? 'That move is not allowed',
                      );
                    }
                  }}
                >
                  {STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {WORK_ITEM_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              </Can>
              <Badge variant={WORK_ITEM_PRIORITY_VARIANT[item.priority]}>
                {WORK_ITEM_PRIORITY_LABELS[item.priority]}
              </Badge>
              {item.addedMidSprint && (
                <Badge variant="warning">Added mid-sprint</Badge>
              )}
              <Can permission="project.task.update">
                <Button size="sm" variant="outline" className="ml-auto" onClick={() => setEditing(true)}>
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </Button>
              </Can>
            </div>

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
            )}

            <div className="grid gap-4 sm:grid-cols-4">
              <Field label="Assignee" value={item.assignee?.name ?? 'Unassigned'} />
              <Field label="Reporter" value={item.reporter?.name ?? '—'} />
              <Field
                label="Estimate"
                value={item.estimateHours !== null ? `${item.estimateHours}h` : 'Not estimated'}
              />
              <Field
                label="Logged"
                value={formatMinutes(item.loggedMinutes)}
                tone={
                  item.estimateHours !== null && item.loggedHours > item.estimateHours
                    ? 'warning'
                    : undefined
                }
              />
            </div>

            {item.description && (
              <div>
                <h3 className="mb-1.5 text-sm font-semibold">Description</h3>
                <p className="whitespace-pre-line text-sm text-muted-foreground">
                  {item.description}
                </p>
              </div>
            )}

            {item.sourceTicket && (
              <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm">
                <LifeBuoy className="h-4 w-4 text-primary" />
                <span>
                  Raised from ticket{' '}
                  <Link
                    to={`/service/tickets/${item.sourceTicket.id}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {item.sourceTicket.number}
                  </Link>{' '}
                  — {item.sourceTicket.subject}
                </span>
              </div>
            )}

            {item.links && item.links.length > 0 && (
              <div>
                <h3 className="mb-1.5 text-sm font-semibold">Linked items</h3>
                <ul className="space-y-1">
                  {item.links.map((link) => (
                    <li key={link.id} className="flex items-center gap-2 text-sm">
                      <span className="text-xs text-muted-foreground">
                        {link.linkType.replace(/_/g, ' ').toLowerCase()}
                      </span>
                      <span className="font-mono text-xs">{link.item.key}</span>
                      <span className="truncate">{link.item.title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ── Time ─────────────────────────────────────────────────── */}
            <div className="rounded-lg border p-3">
              <div className="mb-2 flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold">Log time</h3>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">How long</Label>
                  <Input
                    className="h-8 w-24"
                    placeholder="1.5 or 1:30"
                    value={hours}
                    onChange={(event) => setHours(event.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Activity</Label>
                  <Select
                    className="h-8 w-auto text-xs"
                    value={activityKind}
                    onChange={(event) => setActivityKind(event.target.value)}
                  >
                    {Object.entries(ACTIVITY_KIND_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="min-w-[10rem] flex-1 space-y-1.5">
                  <Label className="text-xs">What you did</Label>
                  <Input
                    className="h-8"
                    value={logNote}
                    onChange={(event) => setLogNote(event.target.value)}
                  />
                </div>
                <Button
                  size="sm"
                  disabled={logTime.isPending || parseHoursInput(hours) === null}
                  onClick={async () => {
                    const minutes = parseHoursInput(hours);
                    if (!minutes) return;
                    await logTime.mutateAsync({
                      date: new Date().toISOString().slice(0, 10),
                      minutes,
                      description: logNote || undefined,
                      activityKind,
                      projectId: item.projectId,
                      workItemId: item.id,
                    });
                    setHours('');
                    setLogNote('');
                  }}
                >
                  Log
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    startTimer.mutate({
                      projectId: item.projectId,
                      workItemId: item.id,
                      activityKind,
                    })
                  }
                >
                  <Play className="h-3.5 w-3.5" />
                  Start timer
                </Button>
              </div>

              {item.timeEntries && item.timeEntries.length > 0 && (
                <ul className="mt-3 space-y-1 border-t pt-3">
                  {item.timeEntries.slice(0, 8).map((entry) => (
                    <li key={entry.id} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        {formatDate(entry.date)} · {entry.user.name}
                        {entry.description && ` — ${entry.description}`}
                      </span>
                      <span className="tabular">{formatMinutes(entry.minutes)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* ── Comments ─────────────────────────────────────────────── */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold">
                  Comments {item.comments?.length ? `(${item.comments.length})` : ''}
                </h3>
              </div>

              <div className="space-y-3">
                {(item.comments ?? []).map((entry) => (
                  <div key={entry.id} className="rounded-lg border p-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm font-medium">{entry.author?.name ?? 'Someone'}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(entry.createdAt)}
                      </span>
                    </div>
                    <p className="mt-1 whitespace-pre-line text-sm">{entry.body}</p>
                  </div>
                ))}

                <div className="space-y-2">
                  <Textarea
                    rows={2}
                    placeholder="Add a comment…"
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                  />
                  <Button
                    size="sm"
                    disabled={comment.isPending || body.trim().length === 0}
                    onClick={async () => {
                      await comment.mutateAsync({ body });
                      setBody('');
                    }}
                  >
                    Comment
                  </Button>
                </div>
              </div>
            </div>

            {/* ── History ──────────────────────────────────────────────── */}
            {item.statusHistory && item.statusHistory.length > 1 && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <RotateCcw className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-semibold">How it moved</h3>
                </div>
                <ul className="space-y-1.5">
                  {item.statusHistory.map((entry) => (
                    <li key={entry.id} className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">
                        {formatDateTime(entry.changedAt)}
                      </span>
                      <span>
                        {entry.fromStatus
                          ? `${WORK_ITEM_STATUS_LABELS[entry.fromStatus]} → `
                          : 'created as '}
                        <span className="font-medium">
                          {WORK_ITEM_STATUS_LABELS[entry.toStatus]}
                        </span>
                      </span>
                      {entry.durationSeconds !== null && (
                        <span className="text-muted-foreground">
                          (after {formatMinutes(Math.round(entry.durationSeconds / 60))})
                        </span>
                      )}
                      {entry.changedBy && (
                        <span className="ml-auto text-muted-foreground">{entry.changedBy.name}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {editing && (
        <WorkItemDialog
          projectId={item.projectId}
          item={item}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}

function Field({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'warning';
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('mt-0.5 text-sm font-medium', tone === 'warning' && 'text-amber-600')}>
        {value}
      </div>
    </div>
  );
}
